import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  Settings, 
  Link as LinkIcon, 
  CheckCircle2, 
  XCircle,
  RefreshCw,
  Database,
  Info
} from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { toast } from "sonner";

export function IntegrationSetupPage() {
  const [financialsEnabled, setFinancialsEnabled] = useState(false);
  const [dimensionsEnabled, setDimensionsEnabled] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Load settings from localStorage on mount
  useEffect(() => {
    const financials = localStorage.getItem("integration.financials.enabled") === "true";
    const dimensions = localStorage.getItem("integration.dimensions.enabled") === "true";
    setFinancialsEnabled(financials);
    setDimensionsEnabled(dimensions);
  }, []);

  const handleSave = async () => {
    setIsSaving(true);
    
    // Simulate API call
    await new Promise((resolve) => setTimeout(resolve, 1000));
    
    // Save to localStorage
    localStorage.setItem("integration.financials.enabled", financialsEnabled.toString());
    localStorage.setItem("integration.dimensions.enabled", dimensionsEnabled.toString());
    
    setIsSaving(false);
    toast.success("Integration settings saved successfully");
  };

  const handleTestConnection = async (module: "financials" | "dimensions") => {
    toast.info(`Testing connection to ${module}...`);
    
    // Simulate connection test
    await new Promise((resolve) => setTimeout(resolve, 1500));
    
    toast.success(`${module} connection test successful`);
  };

  return (
    <div className="container mx-auto p-6 max-w-5xl">
      <div className="mb-6">
        <h1 className="text-3xl font-bold flex items-center gap-2">
          <Settings className="h-8 w-8" />
          Integration Setup
        </h1>
        <p className="text-muted-foreground mt-2">
          Configure integrations with Financials and Dimensions modules to enable GL account synchronisation
        </p>
      </div>

      {/* Financials Integration */}
      <Card className="p-6 mb-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <Database className="h-5 w-5 text-primary" />
              <h2 className="text-xl font-semibold">Financials Module</h2>
              {financialsEnabled ? (
                <Badge variant="success" className="gap-1">
                  <CheckCircle2 className="h-3 w-3" />
                  Connected
                </Badge>
              ) : (
                <Badge variant="secondary" className="gap-1">
                  <XCircle className="h-3 w-3" />
                  Disconnected
                </Badge>
              )}
            </div>
            <p className="text-sm text-muted-foreground">
              Connect to your ERP's Financials module to sync GL accounts for stock item analysis codes
            </p>
          </div>
        </div>

        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
            <div className="flex items-center gap-3">
              <Switch
                id="financials-enabled"
                checked={financialsEnabled}
                onCheckedChange={setFinancialsEnabled}
              />
              <div>
                <Label htmlFor="financials-enabled" className="cursor-pointer">
                  Enable Financials Integration
                </Label>
                <p className="text-xs text-muted-foreground mt-1">
                  Sync GL accounts from your ERP system
                </p>
              </div>
            </div>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <Info className="h-4 w-4 text-muted-foreground" />
                </TooltipTrigger>
                <TooltipContent>
                  <p className="max-w-xs">
                    When enabled, analysis code fields will display GL accounts from your ERP
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>

          {financialsEnabled && (
            <div className="space-y-3 pl-4 border-l-2 border-primary">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <Label className="text-xs text-muted-foreground">ERP System</Label>
                  <p className="font-medium">Access Financials</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Last Sync</Label>
                  <p className="font-medium">{new Date().toLocaleString()}</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">GL Accounts</Label>
                  <p className="font-medium">25 accounts synced</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Status</Label>
                  <p className="font-medium text-green-600">Active</p>
                </div>
              </div>
              
              <Button
                variant="outline"
                size="sm"
                className="gap-2"
                onClick={() => handleTestConnection("financials")}
              >
                <RefreshCw className="h-4 w-4" />
                Test Connection
              </Button>
            </div>
          )}
        </div>
      </Card>

      {/* Dimensions Integration */}
      <Card className="p-6 mb-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <LinkIcon className="h-5 w-5 text-primary" />
              <h2 className="text-xl font-semibold">Dimensions Module</h2>
              {dimensionsEnabled ? (
                <Badge variant="success" className="gap-1">
                  <CheckCircle2 className="h-3 w-3" />
                  Connected
                </Badge>
              ) : (
                <Badge variant="secondary" className="gap-1">
                  <XCircle className="h-3 w-3" />
                  Disconnected
                </Badge>
              )}
            </div>
            <p className="text-sm text-muted-foreground">
              Connect to your ERP's Dimensions module for advanced analysis and reporting
            </p>
          </div>
        </div>

        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
            <div className="flex items-center gap-3">
              <Switch
                id="dimensions-enabled"
                checked={dimensionsEnabled}
                onCheckedChange={setDimensionsEnabled}
              />
              <div>
                <Label htmlFor="dimensions-enabled" className="cursor-pointer">
                  Enable Dimensions Integration
                </Label>
                <p className="text-xs text-muted-foreground mt-1">
                  Sync dimension codes and GL accounts
                </p>
              </div>
            </div>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <Info className="h-4 w-4 text-muted-foreground" />
                </TooltipTrigger>
                <TooltipContent>
                  <p className="max-w-xs">
                    Enables multi-dimensional analysis with GL account integration
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>

          {dimensionsEnabled && (
            <div className="space-y-3 pl-4 border-l-2 border-primary">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <Label className="text-xs text-muted-foreground">ERP System</Label>
                  <p className="font-medium">Access Dimensions</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Last Sync</Label>
                  <p className="font-medium">{new Date().toLocaleString()}</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Dimensions</Label>
                  <p className="font-medium">3 dimensions configured</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Status</Label>
                  <p className="font-medium text-green-600">Active</p>
                </div>
              </div>
              
              <Button
                variant="outline"
                size="sm"
                className="gap-2"
                onClick={() => handleTestConnection("dimensions")}
              >
                <RefreshCw className="h-4 w-4" />
                Test Connection
              </Button>
            </div>
          )}
        </div>
      </Card>

      {/* Information Card */}
      <Card className="p-6 mb-6 bg-blue-50 dark:bg-blue-950/30 border-blue-200 dark:border-blue-800">
        <div className="flex gap-3">
          <Info className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-semibold text-blue-900 dark:text-blue-100 mb-2">
              How Integration Works
            </h3>
            <ul className="text-sm text-blue-800 dark:text-blue-200 space-y-1">
              <li>• When integration is enabled, analysis code fields become dropdown selectors</li>
              <li>• GL accounts are automatically synced from your connected ERP system</li>
              <li>• Only active GL accounts are displayed in the dropdowns</li>
              <li>• Changes are reflected immediately in Stock Items → Analysis Tab</li>
              <li>• If connection fails, the system falls back to manual text input</li>
            </ul>
          </div>
        </div>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end gap-3">
        <Button
          variant="outline"
          onClick={() => {
            setFinancialsEnabled(localStorage.getItem("integration.financials.enabled") === "true");
            setDimensionsEnabled(localStorage.getItem("integration.dimensions.enabled") === "true");
            toast.info("Settings reset to last saved state");
          }}
        >
          Reset
        </Button>
        <Button
          onClick={handleSave}
          disabled={isSaving}
          className="gap-2"
        >
          {isSaving ? (
            <>
              <RefreshCw className="h-4 w-4 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <CheckCircle2 className="h-4 w-4" />
              Save Settings
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
